import { create } from 'zustand';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from './auth';
import { useToastStore } from './toast';
import { memberDedupApi, MyDuplicateCandidate } from '../lib/memberDedup';
import { usersApi } from '../lib/users';

/**
 * "확인이 필요한 항목" — 상단 바와 알림 벨 상단 섹션이 **함께 읽는 정본 한 벌**.
 *
 * 결정 기록 `docs/decisions/2026-08-19-banner-slot-policy-and-actionable-items.md`:
 *  - 2항: 렌더가 두 곳이어도 정본이 하나면 드리프트는 구조적으로 불가능하다. 그래서 두 표면은
 *    자기 상태를 갖지 않고 `useActionItems()` 하나만 부른다.
 *  - 1항: 상시 배너 자격은 **차단성 2종**(빙의 · 기관 인증)뿐이고, 나머지는 여기 항목으로 내린다.
 *
 * ⚠️ 불변식은 「조치해야 사라짐」이 **아니다** — 「**읽는 것만으로는 사라지지 않는다**」다
 * (개정 2). 두 항목 모두 「확인했음」 경로를 갖기 때문이고(번호 회수는 서버가 `phoneRemovedAt`을
 * 비우고, 중복 인원은 후보별 「내가 아님」이 그 역할이다), 알림과 갈리는 축은 그쪽이다 —
 * 알림은 **읽으면** 사라지고 원인이 그대로여도 목록에서 빠진다.
 */

/** 항목 하나의 조치(CTA) — 라벨 + 그 자리에서 수행할 동작. */
export interface ActionItemAction {
  label: string;
  run: () => void;
}

/** 「확인했음」(선택) — 원인을 해소하지 않고 항목만 내리는 경로. 있는 항목만 렌더한다. */
export interface ActionItemAcknowledge {
  label: string;
  run: () => Promise<void>;
}

export type ActionItemId = 'phone-removed' | 'my-duplicates';

export interface ActionItem {
  /** 안정 키 — 렌더 순서·개수와 무관하게 같은 항목을 가리킨다. */
  id: ActionItemId;
  /**
   * 정렬용(오름차순). **번호 회수가 내 중복 인원보다 위**다 — 번호 회수는 계정 도용 신호일 수
   * 있어(결정 기록 「감수하는 것」) 즉시성이 더 필요하다.
   */
  priority: number;
  /** 상단 바 문구 — 좁은 전폭 한 줄에 들어가야 하므로 짧다. */
  barLabel: string;
  /**
   * 벨 섹션 문구 — 바와도, **알림과도** 달라야 한다(결정 기록 4항: 문구가 같아지면 이 설계는
   * 실패다). 알림은 시각·이력 어휘(`auth.service.ts` 「휴대폰 번호가 해제되었습니다」)를 쓰고
   * 항목은 **지금 참인 상태·남은 할 일** 어휘를 쓴다.
   */
  bellLabel: string;
  cta: ActionItemAction;
  acknowledge?: ActionItemAcknowledge;
}

interface ActionItemsState {
  /** 내 중복 인원 후보 — 서버가 준 목록 그대로. 두 표면이 이 한 벌을 본다. */
  duplicates: MyDuplicateCandidate[];
  /** `ensureLoaded`가 이미 조회한 사용자 — 표면이 둘이어도 조회는 한 번만 돈다. */
  loadedForUserId: string | null;
  /** 중복 인원 판정 모달 — 바·벨 어느 쪽에서도 열 수 있어 상태를 store가 소유한다. */
  duplicateModalOpen: boolean;

  ensureLoaded: (userId: string | undefined) => void;
  removeDuplicate: (memberId: string) => void;
  openDuplicateModal: () => void;
  closeDuplicateModal: () => void;
  reset: () => void;
}

export const useActionItemsStore = create<ActionItemsState>((set, get) => ({
  duplicates: [],
  loadedForUserId: null,
  duplicateModalOpen: false,

  /**
   * 로그인 사용자의 중복 후보를 한 번만 조회한다(멱등). 두 표면이 각자 부르므로 멱등성이
   * 필수고, 사용자가 바뀌면 다시 조회한다.
   *
   * 안내는 best-effort — 실패하면 조용히 0건으로 둔다(이관 전 배너와 같은 정책).
   */
  ensureLoaded: (userId) => {
    if (!userId) {
      // 로그아웃 상태에서는 조회하지 않는다(요청도 보내지 않는다).
      if (get().loadedForUserId !== null || get().duplicates.length > 0) {
        set({ duplicates: [], loadedForUserId: null, duplicateModalOpen: false });
      }
      return;
    }
    if (get().loadedForUserId === userId) return;
    set({ loadedForUserId: userId });
    memberDedupApi
      .listMyDuplicates()
      .then((list) => {
        // 조회 도중 사용자가 바뀌었으면 늦게 온 응답을 버린다.
        if (get().loadedForUserId === userId) set({ duplicates: list });
      })
      .catch(() => {
        if (get().loadedForUserId === userId) set({ duplicates: [] });
      });
  },

  /** 병합·「내가 아님」으로 판정이 끝난 후보를 목록에서 뺀다. */
  removeDuplicate: (memberId) =>
    set((state) => {
      const duplicates = state.duplicates.filter((c) => c.memberId !== memberId);
      // 마지막 후보를 처리하면 모달도 닫는다 — 열린 채 남겨두면 나중에 후보가 다시 잡힐 때
      // (계정 전환 등) 모달이 저절로 열린 상태로 돌아온다.
      return { duplicates, duplicateModalOpen: duplicates.length > 0 && state.duplicateModalOpen };
    }),

  openDuplicateModal: () => set({ duplicateModalOpen: true }),
  closeDuplicateModal: () => set({ duplicateModalOpen: false }),
  reset: () => set({ duplicates: [], loadedForUserId: null, duplicateModalOpen: false }),
}));

/**
 * 두 표면의 **유일한 입구**. 상단 바(`ActionItemsBar`)와 벨 상단 섹션
 * (`NotificationDropdown`)이 이 훅 하나를 부르므로 목록·문구·CTA가 갈릴 자리가 없다.
 *
 * 항목이 늘어날 자리는 아래 배열 하나다 — 표면은 `barLabel`/`bellLabel`/`cta`만 읽으므로
 * 새 항목을 더해도 바·벨을 고칠 필요가 없다.
 */
export function useActionItems(): ActionItem[] {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const fetchUser = useAuthStore((s) => s.fetchUser);
  const duplicates = useActionItemsStore((s) => s.duplicates);
  const ensureLoaded = useActionItemsStore((s) => s.ensureLoaded);
  const openDuplicateModal = useActionItemsStore((s) => s.openDuplicateModal);

  useEffect(() => {
    ensureLoaded(user?.id);
  }, [user?.id, ensureLoaded]);

  const items: ActionItem[] = [];

  if (user?.phoneRemovedAt) {
    items.push({
      id: 'phone-removed',
      priority: 10,
      barLabel: '전화번호 재등록이 필요합니다',
      bellLabel: '전화번호가 등록되지 않은 상태입니다',
      cta: { label: '전화번호 등록', run: () => navigate('/profile') },
      acknowledge: {
        label: '확인했음',
        /**
         * 서버가 `Member.phoneRemovedAt`을 비워 **영구 해소**한다 — 해제 축은 둘뿐이고
         * (`apps/server/src/users/users.service.ts` JSDoc: 「(1) 닫기 (2) 재등록」) 이것이 (1)이다.
         * 알림 행은 건드리지 않으므로 벨의 이력은 그대로 남는다.
         */
        run: async () => {
          await usersApi.dismissPhoneRemovedAlert();
          await fetchUser();
        },
      },
    });
  }

  if (duplicates.length > 0) {
    items.push({
      id: 'my-duplicates',
      priority: 20,
      barLabel: `연락처가 같은 인원 정보 ${duplicates.length}건을 확인해 주세요`,
      bellLabel: `본인 여부를 판정하지 않은 인원 정보 ${duplicates.length}건`,
      cta: { label: '확인하기', run: openDuplicateModal },
      // acknowledge 없음 — 모달 안의 후보별 「내가 아님」(`dismissMyDuplicate`)이 그 역할이다.
      // 후보마다 판정이 갈릴 수 있어 항목 단위로 한꺼번에 내릴 수 없다.
    });
  }

  return items.sort((a, b) => a.priority - b.priority);
}

/**
 * 「확인했음」 실패 문구 — **여기 한 곳이 소유한다.**
 *
 * 두 표면에 복사하지 않는 이유는 목록·라벨을 이 모듈이 소유하는 것과 같다(결정 기록 2항:
 * 정본이 하나면 드리프트가 구조적으로 불가능하다). 실제로 복사해 뒀더니 **한쪽만 토스트를
 * 띄우는 상태로 갈려 있었다**(2026-10-05 리뷰 적발 — 벨은 띄우고 상단 바는 `catch {}`로
 * 조용했다).
 */
export const ACTION_ITEM_ACKNOWLEDGE_ERROR = '확인 처리에 실패했습니다.';

/**
 * 「확인했음」 실행 — **두 표면이 함께 쓰는 한 벌**(상단 바 · 벨 상단 섹션).
 *
 * 왜 훅으로 모으나: 종전엔 두 표면이 각자 `try/catch` + busy 상태를 들고 있었고, 그래서
 * **같은 CTA가 다르게 동작**했다 — 네트워크 단절·401에서 벨은 토스트를 띄웠지만 상단 바는
 * 아무 말도 하지 않아 「버튼이 고장났다」로 읽히고 **반복 클릭으로 요청이 매번 나갔다**
 * (2026-10-05 리뷰). 피드백을 한 곳이 소유하면 그 갈림이 다시 생길 자리가 없다.
 *
 * ⚠️ `busyItemId`는 **표면별 UI 상태**다(store가 아니라 훅 인스턴스가 들고 있다) — 바와 벨이
 * 동시에 떠 있을 때 한쪽을 누르면 그쪽 버튼만 잠겨야 한다.
 *
 * ⚠️ 실패해도 **항목은 그대로 남는다** — 해소는 서버 상태가 정본이다(`phoneRemovedAt`).
 * 낙관적으로 목록에서 빼면 원인이 남은 채 안내만 사라진다.
 */
export function useActionItemAcknowledge(): {
  busyItemId: ActionItemId | null;
  run: (item: ActionItem) => void;
} {
  const showToast = useToastStore((s) => s.showToast);
  const [busyItemId, setBusyItemId] = useState<ActionItemId | null>(null);

  const run = useCallback(
    (item: ActionItem) => {
      if (!item.acknowledge) return;
      setBusyItemId(item.id);
      void item.acknowledge
        .run()
        .catch(() => {
          showToast(ACTION_ITEM_ACKNOWLEDGE_ERROR, 'error');
        })
        .finally(() => {
          setBusyItemId(null);
        });
    },
    [showToast],
  );

  return { busyItemId, run };
}
